import React from "react";
import { Briefcase, MapPin, Clock, Users, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { JobPosting } from "../../types/job";

const RecentJobs: React.FC = () => {
  const navigate = useNavigate();

  // 임시 데이터
  const recentJobs: JobPosting[] = [
    {
      id: "1",
      title: "Senior Frontend Developer",
      company: "테커 테크",
      location: "서울, 대한민국",
      type: "정규직",
      experience: "3-5년",
      salary: "4,000-6,000만원",
      description:
        "React, TypeScript를 활용한 웹 애플리케이션 개발을 담당할 시니어 프론트엔드 개발자를 모집합니다.",
      requirements: ["React", "TypeScript", "JavaScript", "HTML/CSS"],
      benefits: ["원격근무", "성과급", "교육비 지원", "건강보험"],
      postedAt: "2023-10-15",
      deadline: "2023-11-15",
      applicants: 45,
      isRecommended: true,
      companyLogo:
        "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop",
      isBookmarked: false,
    },
    {
      id: "2",
      title: "Full Stack Developer",
      company: "스타트업 코리아",
      location: "서울, 대한민국",
      type: "정규직",
      experience: "2-4년",
      salary: "3,500-5,000만원",
      description:
        "Node.js와 React를 사용한 풀스택 개발을 담당할 개발자를 모집합니다.",
      requirements: ["Node.js", "React", "MongoDB", "AWS"],
      benefits: ["스톡옵션", "유연근무", "개발환경 지원"],
      postedAt: "2023-10-14",
      deadline: "2023-11-10",
      applicants: 32,
      isRecommended: true,
      companyLogo:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=100&h=100&fit=crop",
      isBookmarked: false,
    },
    {
      id: "3",
      title: "Backend Developer",
      company: "빅테크 코퍼레이션",
      location: "서울, 대한민국",
      type: "정규직",
      experience: "5-7년",
      salary: "6,000-8,000만원",
      description:
        "대규모 시스템 개발 및 운영을 담당할 백엔드 개발자를 모집합니다.",
      requirements: ["Java", "Spring Boot", "MySQL", "Redis", "Kubernetes"],
      benefits: ["경쟁력 있는 연봉", "성과급", "교육비 지원", "건강검진"],
      postedAt: "2023-10-13",
      deadline: "2023-11-20",
      applicants: 78,
      isRecommended: false,
      companyLogo:
        "https://images.unsplash.com/photo-1599305445771-38ae0c7b2505?w=100&h=100&fit=crop",
      isBookmarked: true,
    },
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1일 전";
    if (diffDays < 7) return `${diffDays}일 전`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)}주 전`;
    return `${Math.ceil(diffDays / 30)}개월 전`;
  };

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              최신 채용공고
            </h2>
            <p className="text-xl text-gray-600">
              지금 가장 인기 있는 채용공고를 확인해보세요
            </p>
          </div>
          <button
            onClick={() => navigate("/jobs")}
            className="hidden md:flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            모든 채용공고 보기
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recentJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={job.companyLogo}
                  alt={job.company}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-semibold text-gray-900">{job.title}</h3>
                  <p className="text-gray-600 text-sm">{job.company}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Briefcase className="w-4 h-4" />
                  <span>
                    {job.type} · {job.experience}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium text-green-600">{job.salary}</span>
                </div>
              </div>

              <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                {job.description}
              </p>

              <div className="flex flex-wrap gap-1 mb-4">
                {job.requirements.slice(0, 3).map((req, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {req}
                  </span>
                ))}
                {job.requirements.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    +{job.requirements.length - 3}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{formatDate(job.postedAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{job.applicants}명</span>
                </div>
              </div>

              <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                지원하기
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 md:hidden">
          <button
            onClick={() => navigate("/jobs")}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
          >
            모든 채용공고 보기
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecentJobs;
